export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  locale?: string
): string {
  const hasDecimals = amount % 1 !== 0;
  try {
    return new Intl.NumberFormat(locale || 'en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(amount);
  } catch {
    // Intl.NumberFormat throws RangeError on a non-ISO currency code — fall
    // back to a plain number with the raw code appended so the amount still
    // renders instead of crashing the component tree.
    return `${new Intl.NumberFormat(locale || 'en-US').format(amount)} ${currency}`;
  }
}

/**
 * Estimate money for the SUMMARY surfaces (totals card, document/history
 * cards, album picker). Cross-platform estimate-money convention
 * (Android/Apple parity): FIXED en-US conventions — currency symbol/code
 * BEFORE the amount, comma grouping — regardless of UI language, so the same
 * estimate reads identically on every platform. Always rounded to whole
 * currency units (half-up, no cents, any currency) — estimate figures are AI
 * ballparks where cents are noise. Line-item rows render plain amounts via
 * `formatEstimateAmount`; the currency appears only in the summary.
 * Payments/billing keep exact locale-aware amounts via `formatCurrency`.
 */
export function formatEstimateMoney(
  amount: number,
  currency: string = 'USD'
): string {
  return formatCurrency(Math.round(amount), currency, 'en-US');
}

/**
 * Plain whole-number amount for estimate LINE ITEMS (no currency — the
 * currency renders only in the summary, cross-platform convention): en-US
 * grouping to match `formatEstimateMoney`.
 */
export function formatEstimateAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(Math.round(amount));
}

export function formatDate(date: string | Date, locale?: string): string {
  return new Intl.DateTimeFormat(locale || 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date));
}

export function formatDateTime(date: string | Date, locale?: string): string {
  return new Intl.DateTimeFormat(locale || 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
}

/**
 * Format date and time in a compact format suitable for UI lists
 * Examples:
 * - en: "11/14/2025, 1:13 AM"
 * - ru: "14.11.2025, 01:13"
 * - de: "14.11.2025, 01:13"
 */
export function formatDateTimeCompact(
  date: string | Date,
  locale?: string
): string {
  return new Intl.DateTimeFormat(locale || 'en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
}
